import { isEqual } from 'lodash-es';

import {
	getChapterByPageGroupId,
	getPageByElementId,
	getParentEntityByPageId
} from '@editor2/components/CourseChangesDiff/helpers';
import { NgTraditional } from '@editor2/types/ngTraditional';
import { type ElementType, ElementConfigName } from '@editor2/types/pageElements';
import { type Chapter } from '@editor2/types/chapter';
import { type Conflict, type ReorderConflict, ConflictType } from '@editor2/types/conflict';
import { type CourseState } from '@editor2/types/courseState';
import { type Page } from '@editor2/types/page';
import { type PageGroup } from '@editor2/types/pageGroup';
import { getChapterPages, renumberChapterPages } from '@editor2/reducers/editor/adapters/helpers';

export const getParentByEntityType = (state, entityType: NgTraditional, entityId) => {
	switch (true) {
		case entityType === NgTraditional.PageGroup:
			return getChapterByPageGroupId(state.chapters, entityId);

		case entityType === NgTraditional.Page:
			return (
				getParentEntityByPageId(state.chapters, entityId) ||
				getParentEntityByPageId(state.page_groups, entityId)
			);

		case !!ElementConfigName[entityType]:
			return getPageByElementId(state.pages, entityId);

		default:
			break;
	}
};

export const getParentByAttributeName = ({ attribute, entityId, state }) => {
	switch (true) {
		case attribute === 'page_groups':
			return getChapterByPageGroupId(state.chapters, entityId);

		case attribute === 'pages':
			return (
				getParentEntityByPageId(state.chapters, entityId) ||
				getParentEntityByPageId(state.page_groups, entityId)
			);

		case attribute === 'elements':
			return getPageByElementId(state.pages, entityId);

		default:
			break;
	}
};

export const getEntityStringByNgType = (type: NgTraditional | ElementType) => {
	switch (true) {
		case type === NgTraditional.Chapter:
			return 'chapters';

		case type === NgTraditional.PageGroup:
			return 'page_groups';

		case type === NgTraditional.Page:
			return 'pages';

		case !!ElementConfigName[type]:
			return 'elements';

		default:
			break;
	}
};

export const getReorderConflictIndex = (state: CourseState, parentEntityId: string) => {
	return state.conflicts.findIndex(
		(item) => item.entityId === parentEntityId && item.conflictType === ConflictType.ChildrenReorder
	);
};

export const getIncomingParent = ({ attribute, id, state, nodes = null }) => {
	return (nodes || state.diffNodes).find(
		(node) => !!node[attribute]?.find((item) => item.family_id === id)
	);
};

export const checkPageGroupDuplicates = (state: CourseState, entityId: string) => {
	state.initialCourse.chapters.entities[entityId].page_groups.map((pageGroupId) =>
		removePageGroupDuplicate(pageGroupId, entityId, state.initialCourse)
	);
	state.chapters.entities[entityId].page_groups.map((pageGroupId) =>
		removePageGroupDuplicate(pageGroupId, entityId, state)
	);
};

export const checkChapterPagesDuplicates = (state: CourseState, entityId: string) => {
	state.initialCourse.chapters.entities[entityId].pages.map((pageId) =>
		removePageDuplicate(pageId, entityId, state.initialCourse)
	);
	state.chapters.entities[entityId].pages.map((pageId) =>
		removePageDuplicate(pageId, entityId, state)
	);
};

export const checkPageGroupsPagesDuplicates = (state: CourseState, entityId: string) => {
	state.initialCourse.page_groups.entities[entityId] &&
		state.initialCourse.page_groups.entities[entityId].pages.map((pageId) =>
			removePageDuplicate(pageId, entityId, state.initialCourse)
		);
	state.page_groups.entities[entityId].pages.map((pageId) =>
		removePageDuplicate(pageId, entityId, state)
	);
};

export const checkElementsDuplicates = (state: CourseState, entityId: string) => {
	state.initialCourse.pages.entities[entityId].elements.map((elementId) =>
		removeElementsDuplicate(elementId, entityId, state.initialCourse)
	);
	state.pages.entities[entityId].elements.map((elementId) =>
		removeElementsDuplicate(elementId, entityId, state)
	);
};

export const removePageGroupDuplicate = (pageGroupId: string, entityId: string, state) => {
	state.chapters.ids.map((chapterId) => {
		if (chapterId === entityId) return;

		const { page_groups } = state.chapters.entities[chapterId];
		if (page_groups?.indexOf(pageGroupId) > -1) {
			state.chapters.entities[chapterId].page_groups = page_groups.filter(
				(id) => id !== pageGroupId
			);
		}
	});
};

export const removePageDuplicate = (pageId: string, entityId: string, state) => {
	state.chapters.ids.map((chapterId) => {
		if (chapterId === entityId) return;

		const { page_groups_enabled, pages, page_groups } = state.chapters.entities[chapterId];
		if (page_groups_enabled) {
			page_groups.map((pageGroupId) => {
				if (pageGroupId === entityId) return;

				const { pages } = state.page_groups.entities[pageGroupId];
				if (pages.indexOf(pageId) > -1) {
					state.page_groups.entities[pageGroupId].pages = pages.filter((id) => id !== pageId);
				}
			});
		} else {
			if (pages.indexOf(pageId) > -1) {
				state.chapters.entities[chapterId].pages = pages.filter((id) => id !== pageId);
			}
		}
	});
};

export const removeElementsDuplicate = (elementId: string, entityId: string, state) => {
	const allPagesIds = [].concat(
		...Object.values(state.chapters).map((chapter: Chapter) => getChapterPages(state, chapter))
	);

	allPagesIds.map((pageId) => {
		if (pageId === entityId) return;

		const elements = state.pages.entities[pageId]?.elements;
		if (elements && elements.indexOf(elementId) > -1) {
			state.pages.entities[pageId].elements = elements.filter((id) => id !== elementId);
		}
	});
};

export const restoreDeletedElement = (state: CourseState, elementId: string) => {
	const initParent = getPageByElementId(state.initialCourse.pages, elementId);
	const initIndex = initParent.elements?.indexOf(elementId) || 0;

	state.pages.entities[initParent.family_id].elements = [
		...state.pages.entities[initParent.family_id].elements.slice(0, initIndex),
		elementId,
		...state.pages.entities[initParent.family_id].elements.slice(initIndex)
	];

	state.elements.entities[elementId] = state.initialCourse.elements.entities[elementId];
	state.changes.elements[elementId] = [];
};

export const restoreDeletedPage = (state: CourseState, pageId: string) => {
	const initParent =
		getParentEntityByPageId(state.initialCourse.chapters, pageId) ||
		getParentEntityByPageId(state.initialCourse.page_groups, pageId);
	const initIndex = initParent.pages.indexOf(pageId);

	if (initParent.type === NgTraditional.Chapter) {
		state.chapters.entities[initParent.family_id].pages = [
			...state.chapters.entities[initParent.family_id].pages.slice(0, initIndex),
			pageId,
			...state.chapters.entities[initParent.family_id].pages.slice(initIndex)
		];
	} else if (initParent.type === NgTraditional.PageGroup) {
		state.page_groups.entities[initParent.family_id].pages = [
			...state.page_groups.entities[initParent.family_id].pages.slice(0, initIndex),
			pageId,
			...state.page_groups.entities[initParent.family_id].pages.slice(initIndex)
		];
	}

	state.pages.entities[pageId] = state.initialCourse.pages.entities[pageId];
	state.changes.pages[pageId] = [];
};

export const restoreDeletedPageGroup = (state: CourseState, pageGroupId: string) => {
	const initParent = getChapterByPageGroupId(state.initialCourse.chapters, pageGroupId);
	const initIndex = initParent.page_groups.indexOf(pageGroupId);

	state.chapters.entities[initParent.family_id].page_groups = [
		...state.chapters.entities[initParent.family_id].page_groups.slice(0, initIndex),
		pageGroupId,
		...state.chapters.entities[initParent.family_id].page_groups.slice(initIndex)
	];

	const initialPageGroup = state.initialCourse.page_groups.entities[pageGroupId];
	state.page_groups.entities[pageGroupId] = initialPageGroup;
	state.changes.page_groups[pageGroupId] = [];

	initialPageGroup.pages.map((pageId) => restoreDeletedPage(state, pageId));
};

export const restoreDeletedChapter = (state: CourseState, chapterId: string) => {
	const initialChapter = state.initialCourse.chapters.entities[chapterId];
	const initialChapterIndex = state.initialCourse.chapters.ids.indexOf(chapterId);

	state.chapters.entities[chapterId] = initialChapter;
	state.chapters.ids = [
		...state.chapters.ids.slice(0, initialChapterIndex),
		chapterId,
		...state.chapters.ids.slice(initialChapterIndex)
	];
	state.changes.chapters[chapterId] = [];

	if (initialChapter.page_groups_enabled) {
		initialChapter.page_groups.map((pageGroupId) => {
			restoreDeletedPageGroup(state, pageGroupId);
		});
	} else {
		initialChapter.pages.map((pageId) => restoreDeletedPage(state, pageId));
	}
};

export const removeConflictById = (state: CourseState, conflictId: string) => {
	state.conflicts = state.conflicts.filter((item) => item.conflictId !== conflictId);
};

export const updateConflict = (
	state: CourseState,
	index: number,
	updatedConflict: Conflict | ReorderConflict
) => {
	state.conflicts = [
		...state.conflicts.slice(0, index),
		updatedConflict,
		...state.conflicts.slice(index + 1)
	];
};

export const updateChapterReorderConflictWithIncoming = (state: CourseState, parentId: string) => {
	const reorderConflictIndex = getReorderConflictIndex(state, parentId);

	if (reorderConflictIndex > -1) {
		const reorderConflict = state.conflicts[reorderConflictIndex];
		const updatedConflict = {
			...reorderConflict,
			current: state.chapters.ids
		} as ReorderConflict;

		if (isEqual(updatedConflict.initial, updatedConflict.current)) {
			state.initialCourse.chapters.ids = updatedConflict.incoming;
			state.chapters.ids = updatedConflict.incoming;

			removeConflictById(state, updatedConflict.conflictId);
		} else {
			updateConflict(state, reorderConflictIndex, updatedConflict);
		}
	}
};

export const updatePageGroupReorderConflictWithIncoming = (
	state: CourseState,
	chapterId: string
) => {
	const reorderConflictIndex = getReorderConflictIndex(state, chapterId);

	if (reorderConflictIndex > -1) {
		const reorderConflict = state.conflicts[reorderConflictIndex];
		const updatedConflict = {
			...reorderConflict,
			current: state.chapters.entities[chapterId].page_groups
		} as ReorderConflict;

		if (isEqual(updatedConflict.initial, updatedConflict.current)) {
			state.initialCourse.chapters.entities[chapterId].page_groups = updatedConflict.incoming;
			state.chapters.entities[chapterId].page_groups = updatedConflict.incoming;

			removeConflictById(state, updatedConflict.conflictId);
		} else {
			updateConflict(state, reorderConflictIndex, updatedConflict);
		}
	}
};

export const updatePageReorderConflictWithIncoming = (
	state: CourseState,
	parent: Chapter | PageGroup
) => {
	const reorderConflictIndex = getReorderConflictIndex(state, parent.family_id);

	if (reorderConflictIndex > -1) {
		const reorderConflict = state.conflicts[reorderConflictIndex];
		const updatedConflict = { ...reorderConflict, current: parent.pages } as ReorderConflict;

		if (isEqual(updatedConflict.initial, updatedConflict.current)) {
			if (parent.type === NgTraditional.Chapter) {
				state.initialCourse.chapters.entities[parent.family_id].pages = updatedConflict.incoming;
				state.chapters.entities[parent.family_id].pages = updatedConflict.incoming;
			} else if (parent.type === NgTraditional.PageGroup) {
				state.initialCourse.page_groups.entities[parent.family_id].pages = updatedConflict.incoming;
				state.page_groups.entities[parent.family_id].pages = updatedConflict.incoming;
			}

			removeConflictById(state, updatedConflict.conflictId);
		} else {
			updateConflict(state, reorderConflictIndex, updatedConflict);
		}
	}
};

export const updateElementReorderConflictWithIncoming = (state: CourseState, parent: Page) => {
	const reorderConflictIndex = getReorderConflictIndex(state, parent.family_id);

	if (reorderConflictIndex > -1) {
		const reorderConflict = state.conflicts[reorderConflictIndex];
		const updatedConflict = {
			...reorderConflict,
			current: parent.elements
		} as ReorderConflict;

		if (isEqual(updatedConflict.initial, updatedConflict.current)) {
			state.initialCourse.pages.entities[parent.family_id].elements = updatedConflict.incoming;
			state.pages.entities[parent.family_id].elements = updatedConflict.incoming;

			removeConflictById(state, updatedConflict.conflictId);
		} else {
			updateConflict(state, reorderConflictIndex, updatedConflict);
		}
	}
};

export const renumberAllChaptersPages = (state: CourseState) => {
	state.chapters.ids.map((chapterId) =>
		renumberChapterPages(state, state.chapters.entities[chapterId])
	);
};

export const renumberAllInitialChaptersPages = (state: CourseState) => {
	state.initialCourse.chapters.ids.map((chapterId) =>
		renumberChapterPages(state.initialCourse, state.initialCourse.chapters.entities[chapterId])
	);
};
